type Particle = {
  x: number; y: number; vx: number; vy: number;
  size: number; born: number; life: number; star: boolean; angle: number;
};
type Position = { x: number; y: number; time: number };

/** Decorative only: passive observers never cancel, capture or change gestures. */
export function mountGoldSparkles(): () => void {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = window.matchMedia('(pointer: coarse)');
  let stop: (() => void) | undefined;

  function start() {
    if (motion.matches || stop || !window.PointerEvent) return;
    const canvas = document.createElement('canvas');
    canvas.dataset.goldSparkles = '';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483646;display:block;';
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
    document.body.appendChild(canvas);

    // Cache the soft glow and four-point star once, rather than blur every frame.
    function sprite(star: boolean) {
      const image = document.createElement('canvas');
      image.width = image.height = 32;
      const pen = image.getContext('2d')!;
      const glow = pen.createRadialGradient(16, 16, 0, 16, 16, 15);
      glow.addColorStop(0, 'rgba(255,224,145,0.45)');
      glow.addColorStop(0.3, 'rgba(223,168,59,0.15)');
      glow.addColorStop(1, 'rgba(223,168,59,0)');
      pen.fillStyle = glow;
      pen.fillRect(0, 0, 32, 32);
      pen.fillStyle = star ? 'rgba(212,161,58,0.85)' : 'rgba(185,129,32,0.8)';
      pen.beginPath();
      if (star) {
        for (let i = 0; i < 8; i++) {
          const angle = i * Math.PI / 4, radius = i % 2 ? 1.3 : 5.5;
          const x = 16 + Math.cos(angle) * radius, y = 16 + Math.sin(angle) * radius;
          if (i === 0) pen.moveTo(x, y); else pen.lineTo(x, y);
        }
        pen.closePath();
      } else pen.ellipse(16, 16, 1.1, 4.5, 0, 0, Math.PI * 2);
      pen.fill();
      return image;
    }
    const dot = sprite(false), star = sprite(true);
    // One bright focal glint follows the pointer; fine flecks drift behind it.
    // Both glow and star are cached together to avoid per-frame blur work.
    const cloud = document.createElement('canvas');
    cloud.width = cloud.height = 64;
    const cloudPen = cloud.getContext('2d')!;
    const gold = cloudPen.createRadialGradient(32, 32, 0, 32, 32, 31);
    gold.addColorStop(0, 'rgba(255,250,220,0.95)');
    gold.addColorStop(0.09, 'rgba(255,236,155,0.9)');
    gold.addColorStop(0.24, 'rgba(235,176,51,0.52)');
    gold.addColorStop(0.55, 'rgba(229,170,50,0.13)');
    gold.addColorStop(1, 'rgba(229,170,50,0)');
    cloudPen.fillStyle = gold;
    cloudPen.fillRect(0, 0, 64, 64);
    cloudPen.fillStyle = '#fff5cc';
    cloudPen.beginPath();
    cloudPen.moveTo(32, 25); cloudPen.lineTo(33.3, 30.7);
    cloudPen.lineTo(39, 32); cloudPen.lineTo(33.3, 33.3);
    cloudPen.lineTo(32, 39); cloudPen.lineTo(30.7, 33.3);
    cloudPen.lineTo(25, 32); cloudPen.lineTo(30.7, 30.7);
    cloudPen.closePath(); cloudPen.fill();
    let halo: { x: number; y: number; targetX: number; targetY: number; born: number; small: boolean; emitting: boolean } | null = null;
    let particles: Particle[] = [], frame = 0, last: Position | null = null;
    let lastFrame = 0, lastSpark = 0;
    let pointerTouch = false, touchId: number | null = null, multiTouch = false;
    let penId: number | null = null;
    let width = 0, height = 0;

    function resetDrawing() {
      window.cancelAnimationFrame(frame); frame = 0;
      particles = []; last = null; halo = null; lastFrame = 0; lastSpark = 0;
      ctx!.clearRect(0, 0, width, height);
    }
    function clear() {
      resetDrawing();
      pointerTouch = false; touchId = null; penId = null; multiTouch = false;
    }
    function resize() {
      resetDrawing();
      const ratio = Math.min(window.devicePixelRatio || 1, coarse.matches ? 1 : 1.5);
      width = window.innerWidth; height = window.innerHeight;
      canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
      ctx!.setTransform(ratio, 0, 0, ratio, 0, 0);
    }
    function draw(now: number) {
      frame = 0;
      ctx!.clearRect(0, 0, width, height);
      const elapsed = lastFrame ? Math.min(40, Math.max(0, now - lastFrame)) : 16;
      lastFrame = now;
      if (halo) {
        const sinceInput = Math.max(0, now - halo.born);
        const age = Math.max(0, (sinceInput - 80) / 240);
        if (age >= 1) halo = null;
        else {
          // Frame-rate-independent easing follows high- and low-rate input with
          // the same short, fluid lag instead of jumping between mouse events.
          const follow = 1 - Math.exp(-elapsed / 45);
          const dx = (halo.targetX - halo.x) * follow;
          const dy = (halo.targetY - halo.y) * follow;
          halo.x += dx; halo.y += dy;
          if (!multiTouch && halo.emitting && sinceInput < 110 && Math.hypot(dx, dy) > 0.45
            && now - lastSpark >= (halo.small ? 55 : 28)) {
            scatter(halo.x, halo.y, halo.small, 1, now, dx, dy);
            lastSpark = now;
          }
          const size = halo.small ? 32 : 38;
          ctx!.save();
          ctx!.globalAlpha = Math.pow(1 - age, 1.3);
          ctx!.drawImage(cloud, halo.x - size / 2, halo.y - size / 2, size, size);
          ctx!.restore();
        }
      }
      particles = particles.filter(p => now - p.born < p.life);
      for (const p of particles) {
        const age = Math.max(0, (now - p.born) / p.life), seconds = Math.max(0, (now - p.born) / 1000);
        const size = p.size * (1 - age * 0.55);
        ctx!.save();
        ctx!.globalAlpha = Math.pow(1 - age, 1.35) * 0.8;
        ctx!.translate(p.x + p.vx * seconds, p.y + p.vy * seconds + 12 * seconds * seconds);
        ctx!.rotate(p.angle + seconds * 0.3);
        ctx!.drawImage(p.star ? star : dot, -size * 2, -size * 2, size * 4, size * 4);
        ctx!.restore();
      }
      if (particles.length || halo) frame = window.requestAnimationFrame(draw);
    }
    function scatter(x: number, y: number, small: boolean, count: number, now: number, dx = 0, dy = 0) {
      for (let i = 0; i < count; i++) {
        const isStar = (count > 1 && i === 0) || Math.random() < 0.14;
        particles.push({
          x: x + (Math.random() - 0.5) * 8, y: y + (Math.random() - 0.5) * 8,
          vx: (Math.random() - 0.5) * 42 - Math.max(-8, Math.min(8, dx)),
          vy: (Math.random() - 0.5) * 38 - Math.max(-8, Math.min(8, dy)) - 4,
          size: (isStar ? 2.8 : 2.6) + Math.random() * 0.7,
          born: now, life: 500 + Math.random() * 250, star: isStar, angle: Math.random() * Math.PI,
        });
      }
      const cap = small ? 18 : 32;
      if (particles.length > cap) particles.splice(0, particles.length - cap);
    }
    function emit(x: number, y: number, touch: boolean, burst = false) {
      if (document.hidden || multiTouch || x < 0 || y < 0 || x > width || y > height) return;
      const now = performance.now(), small = touch || coarse.matches;
      const distance = last ? Math.hypot(x - last.x, y - last.y) : 0;
      if (!burst && last && distance < 0.1) return;
      if (!halo || now - halo.born > 320) halo = { x, y, targetX: x, targetY: y, born: now, small, emitting: true };
      else Object.assign(halo, { targetX: x, targetY: y, born: now, small, emitting: true });
      if (burst) scatter(x, y, small, 6, now);
      last = { x, y, time: now };
      if (!frame) frame = window.requestAnimationFrame(draw);
    }
    function down(event: PointerEvent) {
      if (event.pointerType === 'mouse' || event.button !== 0) return;
      if (!event.isPrimary) { multiTouch = true; pointerTouch = false; last = null; return; }
      if (event.pointerType === 'touch') pointerTouch = true;
      else penId = event.pointerId;
      last = null;
      emit(event.clientX, event.clientY, true, true);
    }
    function move(event: PointerEvent) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse') emit(event.clientX, event.clientY, false);
      else if (event.pointerType === 'touch' ? pointerTouch : event.pointerId === penId)
        emit(event.clientX, event.clientY, true);
    }
    function up(event: PointerEvent) {
      if (event.pointerType !== 'mouse' && halo) halo.emitting = false;
      if (event.pointerType === 'touch') { pointerTouch = false; touchId = null; }
      if (event.pointerId === penId) penId = null;
      last = null;
    }
    function cancel(event: PointerEvent) {
      if (halo) halo.emitting = false;
      if (event.pointerType === 'touch') pointerTouch = false;
      if (event.pointerId === penId) penId = null;
      last = null;
    }
    function touchStart(event: TouchEvent) {
      if (event.touches.length !== 1) { multiTouch = true; touchId = null; pointerTouch = false; last = null; }
      else if (!multiTouch) touchId = event.touches[0].identifier;
    }
    function touchMove(event: TouchEvent) {
      // Browsers cancel Pointer Events when native scrolling takes over. This
      // passive fallback keeps a sparse trail without disabling scroll or pinch.
      if (pointerTouch || multiTouch || event.touches.length !== 1 || touchId === null) return;
      const touch = event.touches[0];
      if (touch.identifier === touchId) emit(touch.clientX, touch.clientY, true);
    }
    function touchEnd(event: TouchEvent) {
      if (!event.touches.length) { touchId = null; multiTouch = false; pointerTouch = false; last = null; if (halo) halo.emitting = false; }
    }
    function leave(event: PointerEvent) { if (event.relatedTarget === null) { last = null; if (halo) halo.emitting = false; } }
    function visibility() { if (document.hidden) clear(); }
    const options = { passive: true, capture: true };
    window.addEventListener('pointerdown', down, options);
    window.addEventListener('pointermove', move, options);
    window.addEventListener('pointerup', up, options);
    window.addEventListener('pointercancel', cancel, options);
    window.addEventListener('pointerout', leave, options);
    window.addEventListener('touchstart', touchStart, options);
    window.addEventListener('touchmove', touchMove, options);
    window.addEventListener('touchend', touchEnd, options);
    window.addEventListener('touchcancel', touchEnd, options);
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', visibility);
    resize();
    stop = () => {
      clear();
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('pointerout', leave, true);
      window.removeEventListener('touchstart', touchStart, true);
      window.removeEventListener('touchmove', touchMove, true);
      window.removeEventListener('touchend', touchEnd, true);
      window.removeEventListener('touchcancel', touchEnd, true);
      window.removeEventListener('resize', resize);
      window.removeEventListener('blur', clear);
      document.removeEventListener('visibilitychange', visibility);
      canvas.remove();
      canvas.width = canvas.height = dot.width = dot.height = star.width = star.height = cloud.width = cloud.height = 0;
      stop = undefined;
    };
  }
  function preference() { if (motion.matches) stop?.(); else start(); }
  motion.addEventListener('change', preference);
  start();
  return () => { motion.removeEventListener('change', preference); stop?.(); };
}
